import { useState } from "react";
import { Link } from "wouter";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/auth-context";
import { useGetMyAccount } from "@workspace/api-client-react";

/**
 * Mirrors the backend's SUBSCRIPTION_WARN_DAYS (billingWarningEmails.ts):
 * the in-app reminder covers the same ~3-day pre-expiry window as the
 * warning email, acting as the second, more visible reminder.
 */
export const EXPIRY_REMINDER_WINDOW_DAYS = 3;

const DAY_MS = 24 * 60 * 60 * 1000;
const STORAGE_KEY = "creative-adapt-plan-expiry-reminder-dismissed";

function readDismissedPeriod(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Decide whether the reminder should show. Exported for tests. The dismissal
 * is keyed to the period end so a dismiss only silences the CURRENT period —
 * a renewed plan's next expiry warns again.
 */
export function shouldShowExpiryReminder(args: {
  status: string | undefined;
  periodEndMs: number;
  nowMs: number;
  dismissedPeriod: string | null;
  periodKey: string;
}): boolean {
  const { status, periodEndMs, nowMs, dismissedPeriod, periodKey } = args;
  if (status !== "active") return false;
  if (Number.isNaN(periodEndMs)) return false;
  const msLeft = periodEndMs - nowMs;
  if (msLeft <= 0) return false;
  if (msLeft > EXPIRY_REMINDER_WINDOW_DAYS * DAY_MS) return false;
  if (dismissedPeriod === periodKey) return false;
  return true;
}

function fmtDate(value: Date | string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("vi-VN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(d);
}

/**
 * Dismissible in-app banner shown to logged-in members whose account's active
 * subscription expires within ~3 days. Renewal is manual (one-off VietQR
 * bank transfer), so the banner deep-links to /purchase. Reviewer-only users
 * never see it (they cannot access the purchase flow).
 */
export function SubscriptionExpiryReminder() {
  const { t } = useTranslation("billing");
  const { user } = useAuth();
  const isMember = !!user && !user.isReviewer;
  const { data: account } = useGetMyAccount({
    query: { enabled: isMember } as never,
  });
  const [dismissedPeriod, setDismissedPeriod] = useState(readDismissedPeriod);

  if (!isMember) return null;
  const subscription = account?.subscription;
  if (!subscription) return null;

  const periodEnd = new Date(subscription.currentPeriodEnd);
  const periodKey = periodEnd.toISOString?.() ?? String(subscription.currentPeriodEnd);
  if (
    !shouldShowExpiryReminder({
      status: subscription.status,
      periodEndMs: periodEnd.getTime(),
      nowMs: Date.now(),
      dismissedPeriod,
      periodKey,
    })
  ) {
    return null;
  }

  const dismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, periodKey);
    } catch {
      // ignore storage failures; the banner just reappears next load
    }
    setDismissedPeriod(periodKey);
  };

  const planLabel = subscription.planLabel ?? subscription.packageKey;

  return (
    <div
      data-testid="banner-plan-expiry"
      role="region"
      aria-label={t("expiryReminder.title")}
      className="flex flex-wrap items-center gap-x-4 gap-y-2"
      style={{
        marginBottom: 18,
        padding: "13px 16px",
        borderRadius: 14,
        border: "1px solid rgba(255,180,60,.4)",
        background: "linear-gradient(135deg,rgba(255,180,60,.14),rgba(255,138,50,.08))",
      }}
    >
      <span style={{ color: "#FFC15A", flex: "none", display: "flex" }} aria-hidden="true">
        <svg
          viewBox="0 0 24 24"
          width="19"
          height="19"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      </span>
      <div style={{ flex: "1 1 320px", minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: "#FFD080" }}>
          {t("expiryReminder.title")}
        </div>
        <p style={{ margin: "2px 0 0", fontSize: 12.5, lineHeight: 1.55, color: "#C0B2A4" }}>
          {t("expiryReminder.message", {
            plan: planLabel,
            date: fmtDate(periodEnd),
          })}
        </p>
      </div>
      <div className="flex items-center" style={{ gap: 8, flex: "none" }}>
        <Link href="/purchase" asChild>
          <a
            data-testid="link-plan-expiry-renew"
            className="transition-[filter] hover:brightness-110"
            style={{
              padding: "8px 15px",
              borderRadius: 10,
              background: "linear-gradient(135deg,#FF8A2B,#FF5900)",
              color: "#fff",
              fontSize: 12.5,
              fontWeight: 600,
              cursor: "pointer",
              boxShadow: "0 6px 14px rgba(255,89,0,.3)",
              whiteSpace: "nowrap",
            }}
          >
            {t("expiryReminder.renew")}
          </a>
        </Link>
        <button
          type="button"
          data-testid="button-plan-expiry-dismiss"
          aria-label={t("expiryReminder.dismiss")}
          title={t("expiryReminder.dismiss")}
          onClick={dismiss}
          className="flex items-center justify-center"
          style={{
            width: 30,
            height: 30,
            borderRadius: 9,
            border: "1px solid rgba(255,255,255,.14)",
            background: "rgba(255,255,255,.05)",
            color: "#C0B2A4",
            cursor: "pointer",
            flex: "none",
            transition: ".15s",
          }}
        >
          <svg
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </div>
  );
}
